import { FieldValues, UseFormSetValue } from 'react-hook-form';
import { DEFAULT_MAX_ITEMS_PER_TRANSACTION } from '@constants/admissions';
import { Admission } from '@domain/admission';
import { CompanyRoles, ROLE_ADMIN } from '@domain/company';
import { Invitation } from '@domain/invitation';
import { TFunction } from 'i18next';

export const emailValidator =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const textValidator = /^[\p{L}\d\s'-]+$/u; // Allows letters, spaces, diacritics, hyphens, and single quotes
export const DATE_PATTERN =
  /^(([0-2]?\d{1})|([3][0,1]{1}))\/[0,1]?\d{1}\/(([1]{1}[9]{1}[0-9]{1}\d{1})|([2]{1}[0]{1}\d{2}))$/;
export const isoCodeValidator =
  /^(aa|ab|ae|af|ak|am|an|ar|as|av|ay|az|az|ba|be|bg|bh|bi|bm|bn|bo|br|bs|ca|ce|ch|co|cr|cs|cu|cv|cy|da|de|dv|dz|ee|el|en|eo|es|et|eu|fa|ff|fi|fj|fo|fr|fy|ga|gd|gl|gn|gu|gv|ha|he|hi|ho|hr|ht|hu|hy|hz|ia|id|ie|ig|ii|ik|io|is|it|iu|ja|jv|ka|kg|ki|kj|kk|kl|km|kn|ko|kr|ks|ku|kv|kw|ky|la|lb|lg|li|ln|lo|lt|lu|lv|mg|mh|mi|mk|ml|mn|mr|ms|mt|my|na|nb|nd|ne|ng|nl|nn|no|nr|nv|ny|oc|oj|om|or|os|pa|pi|pl|ps|pt|qu|rm|rn|ro|ru|rw|sa|sc|sd|se|sg|si|sk|sl|sm|sn|so|sq|sr|ss|st|su|sv|sw|ta|te|tg|th|ti|tk|tl|tn|to|tr|ts|tt|tw|ty|ug|uk|ur|uz|ve|vi|vo|wa|wo|xh|yi|yo|za|zh|zu)$/i;
export const urlValidator = /^https?:\/\/([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
export const numberStartsWithPlus = (value: string) =>
  value.startsWith('+') && !Number.isNaN(Number.parseInt(value.substring(1), 10));
export const numberEndsWithPlus = (value: string) =>
  value.endsWith('+') && !Number.isNaN(Number.parseInt(value.substring(0), 10));
export const isNumberAndNotIncludesPlus = (value: string) =>
  !Number.isNaN(Number.parseInt(value, 10)) && !value.endsWith('+') && !value.startsWith('+');

// Helper function to parse line parts
const parseLineParts = (parts: string[]) => {
  let nameSurname = '';
  let quantity = 1;
  let email: string | null = null;

  parts.forEach((part) => {
    if (part.includes('@')) {
      email = part;
    } else if (isNumberAndNotIncludesPlus(part)) {
      quantity = Number.parseInt(part, 10);
    } else if (numberEndsWithPlus(part)) {
      quantity = Number.parseInt(part.substring(0), 10) + 1;
    } else if (numberStartsWithPlus(part)) {
      quantity = Number.parseInt(part.substring(1), 10) + 1;
    } else {
      nameSurname += `${part} `;
    }
  });

  return { nameSurname: nameSurname.trim(), quantity, email };
};

// Helper function to validate line data
const validateLineData = (
  { nameSurname, quantity, email }: { nameSurname: string; quantity: number; email: string | null },
  {
    t,
    offer,
    roleName,
    enforceQuantityOne,
    enforceEmailRequired,
  }: {
    t: TFunction;
    offer?: Admission | Invitation;
    roleName?: CompanyRoles;
    enforceQuantityOne?: boolean;
    enforceEmailRequired?: boolean;
  }
) => {
  const lineErrors: string[] = [];

  // Name validation
  if (!nameSurname) {
    lineErrors.push(t('errors:missingNameSurname'));
  } else if (!textValidator.test(nameSurname)) {
    lineErrors.push(t('errors:invalidNameSurname'));
  }

  // Email validation
  if (email && !emailValidator.test(email)) {
    lineErrors.push(t('errors:emailFormFieldInvalidFormat'));
  }
  if (enforceEmailRequired && !email) {
    lineErrors.push(t('errors:emailFormFieldRequired'));
  }

  // Quantity validation
  if (Number.isNaN(quantity)) {
    lineErrors.push(t('errors:quantityMustBeNumber'));
  }
  if (enforceQuantityOne && quantity > 1) {
    lineErrors.push(t('errors:quantityMustBeOne'));
  }

  // Offer limits validation
  if (quantity && offer) {
    const isAdmin = roleName === ROLE_ADMIN;
    const maxQuantity = isAdmin ? DEFAULT_MAX_ITEMS_PER_TRANSACTION : offer.itemsPerTransaction.max;

    if (quantity > maxQuantity) {
      lineErrors.push(t('offers:admissionTicketQuantityMaxValue', { maxValue: maxQuantity }));
    }
    if (!isAdmin && quantity < offer.itemsPerTransaction.min) {
      lineErrors.push(t('offers:admissionTicketQuantityMinValue', { minValue: offer.itemsPerTransaction.min }));
    }
  }

  return lineErrors;
};

export const validateMultiInviteText = ({
  value,
  t,
  offer,
  setValue,
  roleName,
  enforceQuantityOne,
  enforceEmailRequired,
}: {
  value: string;
  t: TFunction;
  offer?: Admission | Invitation;
  setValue?: UseFormSetValue<FieldValues>;
  roleName?: CompanyRoles;
  enforceQuantityOne?: boolean;
  enforceEmailRequired?: boolean;
}): true | string => {
  const lines = value.trim().split('\n');
  const isEmpty = lines.length === 0 || (lines.length === 1 && lines[0].trim() === '');

  if (isEmpty) return t('errors:formFieldRequired');
  if (lines.length > 100) return t('errors:tooManyLines');

  const errors: string[] = [];
  let totalNumTickets = 0;

  lines.forEach((line, index) => {
    const parts = line.trim().split(/\s+/);
    const lineData = parseLineParts(parts);

    totalNumTickets += lineData.quantity;

    const lineErrors = validateLineData(lineData, { t, offer, roleName, enforceQuantityOne, enforceEmailRequired });

    if (lineErrors.length > 0) {
      errors.push(`${t('common:line')} ${index + 1}: ${lineErrors.join('. ')}`);
    }
  });

  if (setValue) setValue('quantity', totalNumTickets);

  return errors.length > 0 ? errors.join('\n') : true;
};

export const validateMultiEmail = (
  value: string,
  isRequired: boolean,
  requiredTextError: string,
  emailFieldFormatTextError: string
) => {
  if (!value || value.length === 0) {
    return isRequired ? requiredTextError : true;
  }
  const emails = value.split(',').map((email) => email.trim());
  for (const email of emails) {
    if (!emailValidator.test(email)) {
      return emailFieldFormatTextError;
    }
  }
  return true;
};
